const DAY = 86_400_000;
const days = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / DAY);
/** The age of an item at the end of the range (see LabAge). Catalogue has no publish date and uses the first day it showed up. */
export function labAge(r, ctx) {
    const rangeDays = Math.max(1, days(ctx.from, ctx.to) + 1);
    const pub = r.publishedAt ?? r.firstSeen;
    const ageBasis = r.publishedAt ? 'published' : r.firstSeen ? 'firstSeen' : 'range';
    const ageDays = pub ? Math.max(1, days(pub, ctx.to) + 1) : rangeDays;
    return { ageDays, ageBasis, activeDays: Math.max(1, Math.min(ageDays, rangeDays)) };
}
/** The points of a value on an ingredient's fixed scale, 0..100 (not rounded). */
export function pointsOf(value, ing) {
    let x;
    if (ing.scale === 'log10') {
        if (value <= 0)
            return 0;
        x = Math.log10(value / ing.floor) / Math.log10(ing.ceiling / ing.floor);
    }
    else {
        x = (value - ing.floor) / (ing.ceiling - ing.floor);
    }
    return Math.min(100, Math.max(0, x * 100));
}
/** Scores the rows of one tab: the rows above the tab's minimum, each with its ingredients' values and points and its score, best
 *  first (ties keep the order of the input). */
export function scoreLabRows(rows, def, ctx) {
    const total = def.ingredients.reduce((t, i) => t + i.weight, 0);
    return rows
        .filter(def.minimum)
        .map((r) => {
        const age = labAge(r, ctx);
        const ingredients = {};
        let sum = 0;
        for (const ing of def.ingredients) {
            const value = ing.compute(r, age);
            const points = pointsOf(value, ing);
            ingredients[ing.key] = { value, points };
            sum += ing.weight * points;
        }
        return { r, age, ingredients, score: Math.round(sum / total) };
    })
        .sort((x, y) => y.score - x.score);
}
/** Articles, Projects and Catalogue share the three ingredients and differ only in the scales: a project page is read for its
 *  photos (short visit, many gallery images), an article for its text. */
function contentIngredients(scales, noun) {
    return [
        { key: 'viewsPerDay', label: 'Views per day', formula: '(page views + gallery views) ÷ days since published, capped at the range', why: `A new ${noun} with 8k views beats an old one with the same views.`, unit: 'perDay', weight: 45, floor: scales.views[0], ceiling: scales.views[1], scale: 'log10', compute: (r, age) => (r.single + r.gallery) / age.activeDays },
        { key: 'timePerVisit', label: 'Time per visit', formula: `seconds spent on the ${noun} and its gallery pages ÷ visits that opened it`, why: 'People who stay longer are more engaged.', unit: 's', weight: 30, floor: scales.time[0], ceiling: scales.time[1], scale: 'linear', compute: (r) => r.durationSec / Math.max(1, r.sessions) },
        { key: 'galleryPull', label: 'Gallery pull', formula: `gallery views ÷ ${noun} page views`, why: `How many photos people open for each visit to the ${noun}.`, unit: '', weight: 25, floor: scales.gallery[0], ceiling: scales.gallery[1], scale: 'linear', compute: (r) => r.gallery / Math.max(1, r.single) },
    ];
}
export const LAB_MIN_VIEWS = 30;
export const LAB_MIN_REACH = 50;
export const LAB_SCORE_DEFS = {
    article: {
        target: 'article', noun: 'article', nounPlural: 'articles', usesAge: true,
        minimum: (r) => r.single + r.gallery >= LAB_MIN_VIEWS,
        cohort: `The top 300 articles by views in the selected dates, with at least ${LAB_MIN_VIEWS} views.`,
        ingredients: contentIngredients({ views: [10, 1000], time: [30, 240], gallery: [0, 6] }, 'article'),
        notes: ['Days since published uses the real publication date. An article with no known date is scored over the selected dates instead.'],
    },
    project: {
        target: 'project', noun: 'project', nounPlural: 'projects', usesAge: true,
        minimum: (r) => r.single + r.gallery >= LAB_MIN_VIEWS,
        cohort: `The top 300 projects by views in the selected dates, with at least ${LAB_MIN_VIEWS} views.`,
        ingredients: contentIngredients({ views: [50, 5000], time: [10, 60], gallery: [1, 10] }, 'project'),
        notes: ['Projects are read for their photos: a visit lasts about 30 seconds on the project page but opens several gallery images, so the scales are not the Articles scales.', 'Days since published uses the real publication date. A project with no known date is scored over the selected dates instead.'],
    },
    catalogue: {
        target: 'catalogue', noun: 'product', nounPlural: 'products', usesAge: true,
        minimum: (r) => r.single + r.gallery >= LAB_MIN_VIEWS,
        cohort: `The top 300 products by views in the selected dates, with at least ${LAB_MIN_VIEWS} views.`,
        ingredients: contentIngredients({ views: [5, 500], time: [20, 160], gallery: [0, 10] }, 'product'),
        notes: ['Products have no real publish date, so the age is counted from the first day the product shows up in our data (marked “~”).'],
    },
    newsletter: {
        target: 'newsletter', noun: 'campaign', nounPlural: 'campaigns', usesAge: false,
        minimum: (r) => (r.delivered ?? 0) > 0,
        cohort: 'The top 300 campaigns by delivered e-mails sent in the selected dates.',
        ingredients: [
            { key: 'clickRate', label: 'Click rate', formula: 'unique clicks ÷ delivered', why: 'The strongest sign a campaign worked: readers went to the site.', unit: '%', weight: 50, floor: 0, ceiling: 0.06, scale: 'linear', compute: (r) => (r.clicksUnique ?? 0) / (r.delivered ?? 1) },
            { key: 'openRate', label: 'Open rate', formula: 'unique opens ÷ delivered', why: 'Did the subject line and sender earn an open.', unit: '%', weight: 30, floor: 0.10, ceiling: 0.40, scale: 'linear', compute: (r) => (r.opensUnique ?? 0) / (r.delivered ?? 1) },
            { key: 'clickToOpen', label: 'Click-to-open', formula: 'unique clicks ÷ unique opens', why: 'Of the people who opened, how many found something worth a click.', unit: '%', weight: 20, floor: 0, ceiling: 0.25, scale: 'linear', compute: (r) => ((r.opensUnique ?? 0) > 0 ? (r.clicksUnique ?? 0) / (r.opensUnique ?? 1) : 0) },
        ],
        notes: ['A campaign is a snapshot, not something that accumulates daily, so there is no "per day" ingredient.'],
    },
    socialMedia: {
        target: 'socialMedia', noun: 'post', nounPlural: 'posts', usesAge: true,
        minimum: (r) => (r.reach ?? 0) >= LAB_MIN_REACH,
        cohort: `The top 300 organic Facebook, Instagram and LinkedIn posts published in the selected dates, ranked by reach, with at least ${LAB_MIN_REACH} reach.`,
        ingredients: [
            { key: 'reachPerDay', label: 'Reach per day', formula: 'reach ÷ days since the post was published, capped at the range', why: 'A new post with the same reach as an old one is doing better.', unit: 'perDay', weight: 45, floor: 100, ceiling: 100_000, scale: 'log10', compute: (r, age) => (r.reach ?? 0) / age.activeDays },
            { key: 'engagementRate', label: 'Engagement rate', formula: '(likes + comments + shares + clicks) ÷ reach', why: 'Reactions of any kind, reported by every channel (Instagram reports no clicks, so a click-based rate would give its posts 0).', unit: '%', weight: 30, floor: 0, ceiling: 0.15, scale: 'linear', compute: (r) => ((r.reach ?? 0) > 0 ? (r.engagement ?? 0) / (r.reach ?? 1) : 0) },
            { key: 'shareCommentRate', label: 'Share and comment rate', formula: '(shares + comments) ÷ total engagement', why: 'Shares and comments take more effort than a like.', unit: '%', weight: 25, floor: 0, ceiling: 0.20, scale: 'linear', compute: (r) => ((r.engagement ?? 0) > 0 ? ((r.shares ?? 0) + (r.comments ?? 0)) / (r.engagement ?? 1) : 0) },
        ],
        notes: [
            'Pinterest and YouTube are not included: they report no reach, so they cannot be scored this way.',
            'Paid posts are not included: their reach is bought, not earned.',
            'The numbers are each post’s lifetime totals as of the last data load. A number the platform does not report shows a dash and counts as 0 inside the formula. LinkedIn counts clicks inside its engagement, so its posts read higher on the engagement rate than Facebook and Instagram.',
        ],
    },
};
//# sourceMappingURL=analyticsLabScore.js.map